import { Platform } from "react-native";
import NetInfo from "@react-native-community/netinfo";
import { onlineManager, QueryClient } from "@tanstack/react-query";

import { installMutationDefaults } from "~/state/mutations";

/**
 * taskscribe is offline-first: the whole cache (a guest's local tasks, or a
 * signed-in user's) is persisted to AsyncStorage (see `query-persist.ts`), and
 * signed-in writes are queued while offline and replayed on reconnect
 * (`state/mutations.ts`). A guest's data never touches the database until they
 * create an account, at which point it is migrated to it.
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Persisted rows render instantly and every mutation invalidates its
      // own queries, so a screen refocus inside this window serves cache
      // rather than hitting the Data API. Cuts refetch egress for a task
      // list that changes a handful of times a day.
      staleTime: 15 * 60_000,
      gcTime: Infinity,
      retry: 1,
      refetchOnWindowFocus: false,
      networkMode: "offlineFirst",
    },
    mutations: {
      networkMode: "offlineFirst",
      retry: 2,
    },
  },
});

// On native, drive online/offline from the OS via NetInfo. On web, react-query's
// built-in window `online`/`offline` listener is already correct.
if (Platform.OS !== "web") {
  onlineManager.setEventListener((setOnline) =>
    NetInfo.addEventListener((state) => {
      setOnline(state.isConnected !== false);
    }),
  );
}

// Register write functions before the persister restores, so a write queued
// offline in a previous session can be replayed by `resumePausedMutations()`.
installMutationDefaults(queryClient);
